import type { CSSProperties } from 'react';
import { ColorType, getColorValue } from '@/theme/colors';
import { TableHeaderSurfaceVariant, TableRowHoverVariant } from '../Csessionpage/sessionTweaks';
import type { CmarkdowntableTextAlign } from './Cmarkdowntable.types';
import { TABLE_TEXT_ALIGN } from './Cmarkdowntable.types';

export const DEFAULT_TABLE_TEXT_ALIGN: CmarkdowntableTextAlign = TABLE_TEXT_ALIGN.Left;
export const RTL_CELL_STYLE: CSSProperties = { direction: 'rtl' };

export const TABLE_HEADER_BACKGROUND = 'linear-gradient(180deg, rgba(102,254,193,0.06), rgba(102,254,193,0.015))';
export const TABLE_HEADER_BORDER_BOTTOM = '1px solid rgba(102,254,193,0.22)';
export const TABLE_HEADER_TEXT_COLOR = '#66FEC1';
export const TABLE_HEADER_SHIMMER_BG = 'linear-gradient(90deg, transparent, rgba(102,254,193,0.4), transparent)';
export const TABLE_BODY_BORDER_BOTTOM = '1px solid rgba(255,255,255,0.04)';
export const TABLE_BODY_TEXT_COLOR = 'rgba(255,255,255,0.88)';
export const TABLE_BODY_HOVER_BG = getColorValue(ColorType.Accent, 0.05);
export const TABLE_BODY_HOVER_TEXT = '#fff';
export const TABLE_ACTIVE_ROW_BACKGROUND = getColorValue(ColorType.Accent, 0.14);
const TABLE_ROW_HOVER_EDGE_WIDTH = '2px';

/** The body row's hover paint, per variant (tweak `tableRowHover`). Whisper is
 *  the shipped rendering, so it is what the mark's `existing` row renders. Each
 *  member is the style applied ONLY while the row is hovered — the OPEN row
 *  keeps TABLE_ACTIVE_ROW_BACKGROUND under every one of them, which is the
 *  relationship each variant is really being judged on. */
export const TABLE_ROW_HOVER_STYLE: Record<TableRowHoverVariant, CSSProperties> = {
  [TableRowHoverVariant.Whisper]: {
    backgroundColor: TABLE_BODY_HOVER_BG,
  },
  [TableRowHoverVariant.Wash]: {
    backgroundColor: getColorValue(ColorType.Accent, 0.1),
  },
  [TableRowHoverVariant.Edge]: {
    backgroundColor: getColorValue(ColorType.Accent, 0.04),
    boxShadow: `inset ${TABLE_ROW_HOVER_EDGE_WIDTH} 0 0 0 ${getColorValue(ColorType.Accent, 0.9)}`,
  },
  [TableRowHoverVariant.NeutralLift]: {
    backgroundColor: getColorValue(ColorType.White, 0.05),
  },
};
export const TABLE_BACKDROP_BLUR = 'blur(4px)';

// Sticky header-row surfaces (tweak f3ef2va): the shipped Classic tint is
// near-transparent, so scrolled body text bled through the header. Painted on
// the sticky ROW element itself — the composited element — rather than per
// cell, so the backdrop-filter has a single reliable surface.
export const TABLE_HEADER_SURFACE_STYLE: Record<
  TableHeaderSurfaceVariant,
  CSSProperties
> = {
  [TableHeaderSurfaceVariant.Classic]: {
    backgroundImage: TABLE_HEADER_BACKGROUND,
    backdropFilter: TABLE_BACKDROP_BLUR,
    WebkitBackdropFilter: TABLE_BACKDROP_BLUR,
  },
  [TableHeaderSurfaceVariant.Plate]: {
    backgroundColor: '#0b100e',
    backgroundImage: 'linear-gradient(180deg, rgba(102,254,193,0.07), rgba(102,254,193,0.02))',
    boxShadow: '0 8px 18px -12px rgba(0,0,0,0.6)',
  },
  [TableHeaderSurfaceVariant.Frost]: {
    backgroundColor: 'rgba(8,12,11,0.72)',
    backgroundImage: 'linear-gradient(180deg, rgba(102,254,193,0.06), rgba(102,254,193,0.015))',
    backdropFilter: 'blur(14px) saturate(140%)',
    WebkitBackdropFilter: 'blur(14px) saturate(140%)',
  },
  [TableHeaderSurfaceVariant.Solid]: {
    backgroundColor: '#0d1210',
    backgroundImage: 'linear-gradient(180deg, rgba(102,254,193,0.05), rgba(102,254,193,0.01))',
  },
  [TableHeaderSurfaceVariant.Ink]: {
    backgroundColor: '#080c0b',
  },
  [TableHeaderSurfaceVariant.Obscure]: {
    backgroundColor: 'rgba(8,12,11,0.88)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  [TableHeaderSurfaceVariant.Veil]: {
    backgroundImage: 'linear-gradient(180deg, rgba(12,16,14,0.78), rgba(8,12,11,0.7))',
    backdropFilter: 'blur(12px) saturate(120%)',
    WebkitBackdropFilter: 'blur(12px) saturate(120%)',
  },
  [TableHeaderSurfaceVariant.Hairline]: {
    backgroundColor: '#0b100e',
    boxShadow: 'inset 0 -1px 0 rgba(102,254,193,0.28)',
  },
  [TableHeaderSurfaceVariant.MintWash]: {
    backgroundColor: '#0b100e',
    backgroundImage: 'linear-gradient(180deg, rgba(102,254,193,0.12), rgba(102,254,193,0.03))',
  },
  [TableHeaderSurfaceVariant.Porcelain]: {
    backgroundColor: '#121816',
    backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.015))',
  },
  // Opus companions (one per Grok surface) — every member opaque or strong
  // frost so scrolled body text stays occluded under the sticky header.
  [TableHeaderSurfaceVariant.Onyx]: {
    backgroundColor: '#050807',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
  },
  [TableHeaderSurfaceVariant.Fog]: {
    backgroundColor: 'rgba(10,14,13,0.9)',
    backdropFilter: 'blur(16px) saturate(115%)',
    WebkitBackdropFilter: 'blur(16px) saturate(115%)',
    boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.06)',
  },
  [TableHeaderSurfaceVariant.Shroud]: {
    backgroundImage: 'linear-gradient(180deg, rgba(9,13,12,0.86), rgba(6,9,8,0.8))',
    backdropFilter: 'blur(14px) saturate(130%)',
    WebkitBackdropFilter: 'blur(14px) saturate(130%)',
    boxShadow: 'inset 0 -1px 0 rgba(255,255,255,0.08)',
  },
  [TableHeaderSurfaceVariant.Underscore]: {
    backgroundColor: '#0a0e0d',
    boxShadow:
      'inset 0 1px 0 rgba(102,254,193,0.18), inset 0 -1px 0 rgba(255,255,255,0.14)',
  },
  [TableHeaderSurfaceVariant.Verdigris]: {
    backgroundColor: '#091110',
    backgroundImage: 'linear-gradient(180deg, rgba(102,254,193,0.16), rgba(102,254,193,0.05))',
    boxShadow: 'inset 0 -1px 0 rgba(102,254,193,0.3)',
  },
  [TableHeaderSurfaceVariant.Slate]: {
    backgroundColor: '#0e1412',
    backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.035), rgba(255,255,255,0.008))',
    boxShadow: '0 8px 18px -12px rgba(0,0,0,0.6)',
  },
  // Premium Ethereal Glass set — opaque / strong frost; soft inset highlights.
  [TableHeaderSurfaceVariant.Doppelrand]: {
    backgroundColor: '#090d0c',
    backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.008))',
    boxShadow: [
      'inset 0 0 0 1px rgba(255,255,255,0.07)',
      'inset 0 0 0 2px rgba(8,12,11,0.95)',
      'inset 0 0 0 3px rgba(102,254,193,0.14)',
      'inset 0 1px 0 rgba(255,255,255,0.1)',
    ].join(', '),
  },
  [TableHeaderSurfaceVariant.Lumen]: {
    backgroundColor: '#080c0b',
    backgroundImage: 'linear-gradient(180deg, rgba(102,254,193,0.14), rgba(102,254,193,0.03) 42%, rgba(0,0,0,0) 70%)',
    boxShadow:
      'inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(102,254,193,0.12)',
  },
  [TableHeaderSurfaceVariant.Filigree]: {
    backgroundColor: '#0a0e0d',
    boxShadow: [
      'inset 0 1px 0 rgba(102,254,193,0.28)',
      'inset 0 2px 0 rgba(255,255,255,0.08)',
      'inset 0 3px 0 rgba(102,254,193,0.12)',
      'inset 0 -1px 0 rgba(255,255,255,0.1)',
    ].join(', '),
  },
  [TableHeaderSurfaceVariant.Eclipse]: {
    backgroundColor: '#050807',
    backgroundImage: 'radial-gradient(120% 90% at 50% 0%, rgba(255,255,255,0.04), rgba(0,0,0,0) 55%)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  [TableHeaderSurfaceVariant.PrismEdge]: {
    backgroundColor: '#070a09',
    boxShadow: [
      'inset 1px 0 0 rgba(102,254,193,0.32)',
      'inset 0 1px 0 rgba(102,254,193,0.22)',
      'inset 2px 0 0 rgba(255,255,255,0.05)',
      'inset 0 2px 0 rgba(255,255,255,0.06)',
    ].join(', '),
  },
};

export const TABLE_HEADER_FONT_SIZE_PX = { Expanded: 11, Compact: 10.5 } as const;
export const TABLE_BODY_FONT_SIZE_PX = { Expanded: 14, Compact: 13 } as const;
export const TABLE_HEADER_PADDING = { Expanded: '13px 20px', Compact: '12px 14px' } as const;
export const TABLE_BODY_PADDING = { Expanded: '13px 20px', Compact: '10px 14px' } as const;
export const TABLE_SIZE_VARIANT = {
  Expanded: 'Expanded',
  Compact: 'Compact',
} as const;
export const TABLE_KEY = {
  Enter: 'Enter',
  Escape: 'Escape',
} as const;

export const POPOVER_MIN_WIDTH = 300;
export const POPOVER_DEFAULT_WIDTH = 460;
export const POPOVER_MIN_HEIGHT = 180;
export const POPOVER_RESIZE_HANDLE_SIZE = 18;
export const POPOVER_VIEWPORT_PADDING = 12;
export const POPOVER_ROW_GAP = 12;
export const POPOVER_EMPTY_VALUE = '-';
export const POPOVER_COLUMN_FALLBACK = 'Column';
export const POPOVER_ACCENT = '#66FEC1';
export const POPOVER_ESTIMATED_HEIGHT = 320;
export const POPOVER_ROW_BELOW_GAP = 8;
export const POPOVER_MOTION_KEY_PREFIX = 'table-row-popover';
export const POPOVER_CLOSE_BACKGROUND = {
  Idle: 'transparent',
  Hover: 'rgba(255,255,255,0.08)',
} as const;
export const POPOVER_CLOSE_COLOR = {
  Idle: 'rgba(255,255,255,0.4)',
  Hover: '#fff',
} as const;
export const POPOVER_HEADER_BORDER_BOTTOM = '1px solid rgba(255,255,255,0.06)';
export const POPOVER_ROW_BORDER_BOTTOM = '1px solid rgba(255,255,255,0.04)';
export const POPOVER_ROW_BORDER_BOTTOM_NONE = 'none';
export const POPOVER_TEXT_TRANSFORM_UPPERCASE = 'uppercase';
export const POPOVER_WORD_BREAK = 'break-word';
export const POPOVER_DRAG_CURSOR = 'move';
export const POPOVER_RESIZE_CURSOR = 'nwse-resize';
export const POPOVER_COPY_FEEDBACK_MS = 1400;
export const POPOVER_COPY_ENTRY_SEPARATOR = '\n';
export const POPOVER_COPY_KEY_VALUE_JOINER = ': ';
export const POPOVER_COPY_LABEL = {
  Idle: 'Copy row details',
  Done: 'Row details copied',
} as const;
export const POPOVER_CLOSE_LABEL = 'Close row details';
export const POPOVER_ACTION_DATA_NAME = {
  Copy: 'popoverCopy',
  Close: 'popoverClose',
} as const;

export const TABLE_MARKDOWN_ALIGN_SEGMENT = {
  Left: ':---',
  Center: ':---:',
  Right: '---:',
} as const;

export const TABLE_EXPORT_OPTION_KEY = {
  Markdown: 'markdown',
  Csv: 'csv',
  Image: 'image',
} as const;

export const TABLE_EXPORT_MIME_TYPE = {
  Csv: 'text/csv;charset=utf-8',
} as const;

export const TABLE_EXPORT_FILENAME = {
  Csv: 'table.csv',
} as const;

/** A copy can be refused by the browser — no clipboard permission, an
 *  unfocused document. The reader has to be told, because the alternative is a
 *  check mark over an empty clipboard. */
export const TABLE_COPY_ERROR_TITLE = 'copy';
export const TABLE_COPY_ERROR_BODY =
  'Could not copy to the clipboard. Your browser may be blocking clipboard access for this page.';

/** Copying the table as an image can fail for reasons the reader cannot see —
 *  a browser that refuses the clipboard write, an image the renderer could not
 *  fetch. Silence reads as a broken button, so the failure is stated. */
export const TABLE_IMAGE_COPY_ERROR_TITLE = 'copy as image';
export const TABLE_IMAGE_COPY_ERROR_BODY =
  'Could not copy the table as an image. Copy it as Markdown or CSV instead.';
export const TABLE_IMAGE_COPY_ERROR_BUTTON_TEXT = 'Got it';

export const TABLE_VISUAL_TYPE = 'Table';
export const TABLE_METRIC_UNIT = 'ROWS';

/** Tables under this many rows open expanded instead of as the compact tile
 *  (issue rglkviz) — small tables are cheaper to read than to click open. */
export const TABLE_DEFAULT_EXPANDED_MAX_ROWS = 10;

export const TABLE_VISUAL_MODE = {
  Compact: 'compact',
} as const;

export const TABLE_DROPDOWN_ALIGN_RIGHT = 'right';
export const TABLE_SCROLL_DIRECTION = {
  Horizontal: 'horizontal',
  Vertical: 'vertical',
} as const;
export const TABLE_CROSS_AXIS_OVERFLOW_VISIBLE = 'visible';

export const TABLE_STYLE_VALUE = {
  TouchActionAuto: 'auto',
  TouchActionNone: 'none',
  UserSelectNone: 'none',
  WidthMaxContent: 'max-content',
  WidthFull: '100%',
  HeightFull: '100%',
  WhiteSpaceNormal: 'normal',
  OverflowWrapBreakWord: 'break-word',
} as const;

export const TABLE_GRID_DISPLAY = 'grid';
export const TABLE_GRID_SUBGRID = 'subgrid';
export const TABLE_GRID_ALIGN_START = 'start';
export const TABLE_GRID_FULL_SPAN = '1 / -1';
export const TABLE_GRID_BACKGROUND_TRANSPARENT = 'transparent';

export const ICON_BTN_SIZE = 32;
export const ICON_BTN_ICON_SIZE = 20;
export const ICON_BTN_RADIUS = 6;
export const ICON_BTN_CONTAINER_STYLE: CSSProperties = {
  width: ICON_BTN_SIZE,
  minWidth: ICON_BTN_SIZE,
};
